"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMasterPeriode } from "@/hooks/use-master-periode";
import { usePeriodeStore } from "@/hooks/use-periode";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CheckCheck,
  Edit2,
  Inbox,
  Info,
  Loader2,
  PenLine,
  Save,
  Search,
  X,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";

// --- Interfaces ---
interface MasterKomponenKegiatan {
  master_komponen_kegiatan_id: number;
  kode_komponen: string;
  komponen_kegiatan: string;
  master_unsur_id: number;
  satuan: string;
  sumber_data: string;
  keterangan_komponen: string;
}

interface MasterPeriodeDetail {
  id: string;
  master_periode_detail_id: number;
  master_periode_id: number;
  kode_komponen: string;
  jml_maks_sks: number;
  bkd: number;
  point_tp: number;
  point_aa: number;
  point_l: number;
  point_lk: number;
  point_gb: number;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

interface DetailItem {
  detail: MasterPeriodeDetail;
  komponen: MasterKomponenKegiatan;
}

interface UnsurGroup {
  master_unsur_id: number;
  kode_unsur: string;
  nama_unsur: string;
  details: DetailItem[];
}

import { fetchMasterPeriodeDetail } from "@/pages/common/remun-dosen/api/get-master-periode-detail";

// --- API FUNCTIONS ---
const fetchMappingDataAPI = async (id: number): Promise<UnsurGroup[]> => {
  const result = await fetchMasterPeriodeDetail(id);
  return (result?.datas || []) as unknown as UnsurGroup[];
};

const updateSingleDetailAPI = async (payload: MasterPeriodeDetail) => {
  // TODO: Implement real update API
  return new Promise<MasterPeriodeDetail>((resolve) =>
    setTimeout(() => resolve(payload), 500),
  );
};

const updateBulkDetailAPI = async (payload: MasterPeriodeDetail[]) => {
  // TODO: Implement real bulk update API
  return new Promise<MasterPeriodeDetail[]>((resolve) =>
    setTimeout(() => resolve(payload), 800),
  );
};

export default function PeriodeKomponenPage() {
  const [searchQuery, setSearchQuery] = useState("");

  // --- PERIOD SYNC ---
  const { activePeriod } = usePeriodeStore();
  const { data: masterPeriodes } = useMasterPeriode();

  const currentPeriodObj = masterPeriodes?.find(
    (p) => `${p.tahun}-${p.semester}` === activePeriod,
  );
  const master_periode_id = currentPeriodObj?.master_periode_id;

  // --- STATE MANAGEMENT UNTUK UI EDIT ---
  const [periodeDetails, setPeriodeDetails] = useState<MasterPeriodeDetail[]>(
    [],
  );
  const [editingRows, setEditingRows] = useState<Record<number, boolean>>({});
  const [draftData, setDraftData] = useState<
    Record<number, MasterPeriodeDetail>
  >({});
  const [isBulkEditing, setIsBulkEditing] = useState(false);

  // --- REACT QUERY: FETCH DATA ---
  const { data, isLoading, isError } = useQuery({
    queryKey: ["periode-komponen-mapping", master_periode_id],
    queryFn: () => fetchMappingDataAPI(master_periode_id!),
    enabled: !!master_periode_id,
  });

  // Sinkronisasi data fetch dengan local state agar bisa diedit
  // Kita ratakan (flatten) semua detail ke dalam array single untuk memudahkan editing logic
  useEffect(() => {
    if (data) {
      const flattened = data.flatMap((group) =>
        group.details.map((item) => item.detail),
      );
      setPeriodeDetails(flattened);
    }
  }, [data]);

  // --- REACT QUERY: MUTATIONS ---
  const mutationSingle = useMutation({
    mutationFn: updateSingleDetailAPI,
    onSuccess: (updatedItem) => {
      setPeriodeDetails((prev) =>
        prev.map((item) =>
          item.master_periode_detail_id === updatedItem.master_periode_detail_id
            ? updatedItem
            : item,
        ),
      );
      handleCancelRow(updatedItem.master_periode_detail_id);
    },
  });

  const mutationBulk = useMutation({
    mutationFn: updateBulkDetailAPI,
    onSuccess: (updatedItems) => {
      const updatedMap = new Map(
        updatedItems.map((obj) => [obj.master_periode_detail_id, obj]),
      );
      setPeriodeDetails((prev) =>
        prev.map(
          (item) => updatedMap.get(item.master_periode_detail_id) || item,
        ),
      );
      setEditingRows({});
      setIsBulkEditing(false);
    },
  });

  // --- HANDLERS UNTUK EDIT SINGLE ROW ---
  const handleEditRow = (detail: MasterPeriodeDetail) => {
    setEditingRows((prev) => ({
      ...prev,
      [detail.master_periode_detail_id]: true,
    }));
    setDraftData((prev) => ({
      ...prev,
      [detail.master_periode_detail_id]: { ...detail },
    }));
  };

  const handleCancelRow = (id: number) => {
    setEditingRows((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  const handleSaveRow = (id: number) => {
    mutationSingle.mutate(draftData[id]);
  };

  // --- HANDLERS UNTUK BULK EDIT ---
  const handleEnableBulkEdit = () => {
    const newEditingRows: Record<number, boolean> = {};
    const newDraftData: Record<number, MasterPeriodeDetail> = {};
    periodeDetails.forEach((detail) => {
      newEditingRows[detail.master_periode_detail_id] = true;
      newDraftData[detail.master_periode_detail_id] = { ...detail };
    });
    setEditingRows(newEditingRows);
    setDraftData(newDraftData);
    setIsBulkEditing(true);
  };

  const handleCancelBulkEdit = () => {
    setEditingRows({});
    setDraftData({});
    setIsBulkEditing(false);
  };

  const handleSaveBulkEdit = () => {
    const payload = Object.values(draftData);
    mutationBulk.mutate(payload);
  };

  const handleInputChange = (
    id: number,
    field: keyof MasterPeriodeDetail,
    value: string,
  ) => {
    const numValue = value === "" ? 0 : parseFloat(value);
    setDraftData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: numValue },
    }));
  };

  // --- FILTERING DATA ---
  const filteredData = (data || [])
    .map((group) => {
      const filteredDetails = group.details.filter(
        (item) =>
          item.komponen.kode_komponen
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          item.komponen.komponen_kegiatan
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
      );
      return { ...group, details: filteredDetails };
    })
    .filter((group) => group.details.length > 0);

  return (
    <div>
      <Card className="shadow-sm border-slate-200 overflow-hidden">
        <CardHeader className="bg-slate-50/80 border-b border-slate-100 pb-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
                Mapping Komponen Periode
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                Konfigurasi poin BKD dan poin Remunerasi per Jabatan Fungsional.
                <br />
                <span className="font-semibold text-blue-600">
                  Periode:{" "}
                  {currentPeriodObj
                    ? `${currentPeriodObj.semester === 1 ? "Ganjil" : "Genap"} ${currentPeriodObj.tahun}/${parseInt(currentPeriodObj.tahun) + 1}`
                    : "..."}
                </span>
              </CardDescription>
            </div>

            {/* Toolbar Area */}
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cari kode atau kegiatan..."
                  className="pl-9 bg-white border-slate-200 focus-visible:ring-blue-500 shadow-sm h-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isBulkEditing || isLoading}
                />
              </div>

              {!isBulkEditing ? (
                <Button
                  onClick={handleEnableBulkEdit}
                  disabled={isLoading || isError}
                  className="w-full sm:w-auto gap-2 bg-slate-800 hover:bg-slate-700"
                >
                  <PenLine className="w-4 h-4" /> Edit Massal
                </Button>
              ) : (
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={handleCancelBulkEdit}
                    variant="outline"
                    className="w-full sm:w-auto gap-2"
                    disabled={mutationBulk.isPending}
                  >
                    <X className="w-4 h-4" /> Batal
                  </Button>
                  <Button
                    onClick={handleSaveBulkEdit}
                    className="w-full sm:w-auto gap-2 bg-blue-600 hover:bg-blue-700"
                    disabled={mutationBulk.isPending}
                  >
                    {mutationBulk.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCheck className="w-4 h-4" />
                    )}
                    Simpan Semua
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Keterangan Fungsional (Legend) */}
          <div className="mt-4 bg-blue-50/50 border border-blue-100 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-2 text-xs text-blue-800">
            <div className="flex items-center gap-1.5 font-semibold text-blue-700">
              <Info className="w-4 h-4" /> Keterangan Jabatan:
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              <span>
                <span className="font-bold">TP</span>: Tenaga Pengajar
              </span>
              <span>
                <span className="font-bold">AA</span>: Asisten Ahli
              </span>
              <span>
                <span className="font-bold">L</span>: Lektor
              </span>
              <span>
                <span className="font-bold">LK</span>: Lektor Kepala
              </span>
              <span>
                <span className="font-bold">GB</span>: Guru Besar
              </span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-white">
                <TableRow className="border-b-slate-200 hover:bg-transparent">
                  <TableHead className="w-12 pl-6 text-slate-600 font-semibold">
                    No
                  </TableHead>
                  <TableHead className="w-24 text-slate-600 font-semibold">
                    Kode
                  </TableHead>
                  <TableHead className="w-100 min-w-100 text-slate-600 font-semibold">
                    Komponen Kegiatan
                  </TableHead>
                  <TableHead className="text-center w-24 text-slate-600 font-semibold">
                    SKS Maks
                  </TableHead>
                  <TableHead className="bg-red-50/50 text-red-700 text-center font-bold border-x border-red-100/60 w-24">
                    BKD
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-20">
                    TP
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-20">
                    AA
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-20">
                    L
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-20">
                    LK
                  </TableHead>
                  <TableHead className="bg-blue-50/40 text-blue-700 text-center font-bold border-r border-blue-100/50 w-20">
                    GB
                  </TableHead>
                  <TableHead className="text-center w-28 text-slate-600 font-semibold pr-6">
                    Aksi
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={11} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400 space-y-3">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                        <p className="text-sm">Memuat data komponen...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {isError && (
                  <TableRow>
                    <TableCell colSpan={11} className="h-64 text-center">
                      <div className="text-red-500 font-medium text-sm">
                        Gagal memuat data dari server.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {(() => {
                  let globalIndex = 0;
                  return !isLoading && !isError && filteredData.length > 0
                    ? filteredData.map((group) => (
                        <Fragment key={group.master_unsur_id}>
                          {/* Baris Kategori Unsur */}
                          <TableRow className="bg-slate-100/80 hover:bg-slate-100/80 border-y border-slate-200">
                            <TableCell
                              colSpan={11}
                              className="font-bold text-slate-800 py-2.5 text-[11px] tracking-widest uppercase pl-6"
                            >
                              {group.nama_unsur}
                            </TableCell>
                          </TableRow>

                          {/* Baris Data Komponen */}
                          {group.details.map((item) => {
                            globalIndex++;
                            const detailId =
                              item.detail.master_periode_detail_id;
                            const isEditing = editingRows[detailId];

                            // Gunakan draftData jika sedang diedit, jika tidak gunakan data langsung dari query (item.detail)
                            // Ini memastikan data selalu sinkron dengan API terbaru.
                            const rowData = isEditing
                              ? draftData[detailId]
                              : item.detail;

                            const isSavingThisRow =
                              mutationSingle.isPending &&
                              mutationSingle.variables
                                ?.master_periode_detail_id === detailId;

                            return (
                              <TableRow
                                key={item.komponen.kode_komponen}
                                className={`${isEditing ? "bg-blue-50/20" : "hover:bg-slate-50"} transition-colors`}
                              >
                                {/* Global Number */}
                                <TableCell className="pl-6 align-middle py-3 text-xs font-medium text-slate-400">
                                  {globalIndex}
                                </TableCell>

                                {/* Static Info */}
                                <TableCell className="align-middle py-3">
                                  <Badge
                                    variant="outline"
                                    className="font-mono text-xs bg-white text-slate-600 border-slate-300 shadow-sm"
                                  >
                                    {item.komponen.kode_komponen}
                                  </Badge>
                                </TableCell>
                                <TableCell className="align-middle py-3 pr-4 w-100 min-w-100">
                                  <div className="flex flex-col gap-1 items-start w-full">
                                    <TooltipProvider>
                                      <Tooltip delayDuration={300}>
                                        <TooltipTrigger asChild>
                                          <div className="text-sm font-medium text-slate-900 leading-tight truncate cursor-help hover:text-blue-600 transition-colors w-full max-w-90 overflow-hidden">
                                            {item.komponen.komponen_kegiatan}
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="right"
                                          className="max-w-87.5 bg-slate-900 text-white border-slate-800 shadow-xl p-3 z-50"
                                        >
                                          <p className="text-xs leading-relaxed">
                                            {item.komponen.komponen_kegiatan}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <Badge
                                      variant="secondary"
                                      className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[9px] uppercase font-semibold px-1.5 py-0 rounded-sm"
                                    >
                                      {item.komponen.satuan}
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-medium text-slate-600 align-middle py-3">
                                  {rowData.jml_maks_sks || (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </TableCell>

                                {/* Editable Columns (BKD, TP, AA, L, LK, GB) */}
                                {[
                                  "bkd",
                                  "point_tp",
                                  "point_aa",
                                  "point_l",
                                  "point_lk",
                                  "point_gb",
                                ].map((field) => {
                                  const isBkd = field === "bkd";
                                  const rawVal = (rowData as any)[field];
                                  const val = Number(rawVal) || 0;

                                  return (
                                    <TableCell
                                      key={field}
                                      className={`text-center align-middle py-2 px-2 
                                    ${isBkd ? "bg-red-50/20 border-x border-red-100/50" : "border-r border-blue-50/50"}`}
                                    >
                                      {isEditing ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          disabled={
                                            mutationBulk.isPending ||
                                            isSavingThisRow
                                          }
                                          className="w-16 h-8 px-1 mx-auto text-center tabular-nums text-sm bg-white border-blue-300 focus-visible:ring-blue-500 disabled:opacity-50"
                                          value={rawVal}
                                          onChange={(e) =>
                                            handleInputChange(
                                              detailId,
                                              field as keyof MasterPeriodeDetail,
                                              e.target.value,
                                            )
                                          }
                                        />
                                      ) : (
                                        <span
                                          className={`tabular-nums ${isBkd ? "font-bold text-red-600" : "font-medium text-blue-600"}`}
                                        >
                                          {val.toLocaleString(undefined, {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      )}
                                    </TableCell>
                                  );
                                })}

                                {/* Action Buttons Column */}
                                <TableCell className="text-center align-middle py-3 pr-6">
                                  {!isBulkEditing &&
                                    (isEditing ? (
                                      <div className="flex items-center justify-center gap-1.5">
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          disabled={isSavingThisRow}
                                          className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                                          onClick={() =>
                                            handleSaveRow(detailId)
                                          }
                                        >
                                          {isSavingThisRow ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <Save className="w-4 h-4" />
                                          )}
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          disabled={isSavingThisRow}
                                          className="h-7 w-7 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                                          onClick={() =>
                                            handleCancelRow(detailId)
                                          }
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 text-slate-400 hover:text-blue-600"
                                        onClick={() =>
                                          handleEditRow(item.detail)
                                        }
                                      >
                                        <Edit2 className="w-4 h-4 mr-2" /> Edit
                                      </Button>
                                    ))}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      ))
                    : !isLoading && !isError && (
                        <TableRow>
                          <TableCell colSpan={11} className="h-64 text-center">
                            <div className="flex flex-col items-center justify-center text-slate-500 space-y-3">
                              <div className="bg-slate-50 p-4 rounded-full border border-slate-100">
                                <Inbox className="w-8 h-8 text-slate-300" />
                              </div>
                              <p className="text-sm text-slate-500">
                                Tidak ada komponen kegiatan yang cocok dengan{" "}
                                <br />
                                <span className="font-semibold text-slate-700">
                                  "{searchQuery}"
                                </span>
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                })()}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
